/*!
 * Curio Cards — Sell Your Cards v3
 * Card marketplace: browse, filter, sort, basket, checkout
 */
(function () {
  'use strict';

  // ── Config ────────────────────────────────────────────────────────────────

  const GAMES = {
    pokemon:   { id: 'pokemon',   label: 'Pokémon',  color: '#FFC107', rgb: '255,193,7',  text: '#111' },
    riftbound: { id: 'riftbound', label: 'Riftbound', color: '#E87B2A', rgb: '232,123,42', text: '#fff' },
  };

  // Near Mint only — we don't buy played cards. (The panel & basket already use NM exclusively.)
  const CONDITIONS = [
    { id: 'NM', label: 'Near Mint', criteria: 'No visible wear — pulled from pack or sleeve.', mult: 1.00, color: '#4ade80', rgb: '74,222,128' },
  ];

  const POSTAGE = [
    { id: 'royal-mail-tracked', label: 'Royal Mail Tracked 48',       desc: 'Tracked delivery — £3.59 deducted from your payout', fee: 3.59 },
    { id: 'royal-mail-signed',  label: 'Royal Mail Tracked & Signed', desc: 'Signature on delivery — recommended over £50 — £4.99 deducted', fee: 4.99 },
    { id: 'self-arranged',      label: "I'll arrange my own tracked postage", desc: 'No fee — post to us with your own tracked service. You keep the full offer.', fee: 0 },
  ];

  const PAYMENT_METHODS = [
    { id: 'paypal',        label: 'PayPal',        desc: 'Instant to your PayPal account · We cover fees' },
    { id: 'store-credit',  label: 'Store Credit',  desc: '10% bonus — receive 90% of market value instead of 80%', badge: 'Best value' },
  ];

  const STORE_CREDIT_UPLIFT = 0.90 / 0.80; // 1.125× — 90% vs 80% of market

  const SORTS = [
    { id: 'price_desc',  label: 'Price: High → Low' },
    { id: 'price_asc',   label: 'Price: Low → High' },
    { id: 'name_asc',    label: 'Name: A–Z' },
    { id: 'name_desc',   label: 'Name: Z–A' },
    { id: 'number_asc',  label: 'Card #: Asc' },
    { id: 'number_desc', label: 'Card #: Desc' },
  ];

  const PAGE_SIZE  = 24;
  const DEBOUNCE   = 500;
  const MIN_ORDER  = 10;

  // ── State ─────────────────────────────────────────────────────────────────

  let state = {
    game: 'pokemon', cards: [], total: 0, totalPages: 0,
    availableSets: [], availableRarities: [], loading: true,
    q: '', page: 1, sort: 'price_desc', filterSet: '', filterRarity: '',
    view: 'browse',
    panelOpen: false, selectedCard: null, selectedCond: 'NM', selectedQty: 1,
    drawerOpen: false,
    basket: [],
    photos: {},        // basket item key → [{ thumb, blob }] (max 2, staged pre-submit)
    photoOpen: {},     // basket item key → bool (photo section expanded)
    photoUpload: null, // { status: 'uploading'|'done'|'failed', pct } — success-screen card (used in Task 10)
    postageMethod: 'royal-mail-tracked',
    paymentMethod: 'paypal',
    paypalSameAsContact: true,  // payout goes to the contact email unless unticked
    paypalEmail: '',
    successPayout: null,        // resolved payout email shown on the success screen
    ageConfirmed: false, // required 18+ declaration before selling

    submitting: false, submitError: null, successRef: null,
    customerName: '', customerEmail: '',
    address: { address1: '', address2: '', city: '', postcode: '', phone: '' },
    successLabel: null, // { labelStatus, trackingNumber, labelPdfUrl }
    successPostTo: null, // Curio's address for the self-arranged success screen
    pricesLastUpdated: null,
    tracker: { ref: '', found: null, loading: false, error: null },
  };

  let apiUrl = '', root = null, zones = {}, searchTimer = null;

  // ── API ───────────────────────────────────────────────────────────────────

  async function apiFetch(path) {
    const res = await fetch(apiUrl + path);
    if (!res.ok) throw new Error(`API ${res.status}`);
    return res.json();
  }

  async function apiPost(path, body) {
    const res = await fetch(apiUrl + path, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  }

  // Upload staged photos after submit. One multipart request; one automatic retry.
  // A 409 means a previous attempt already succeeded (lost response) — treat as done.
  function uploadStagedPhotos(reference, staged, attempt = 1) {
    const fd = new FormData();
    staged.forEach(p => fd.append(`photo_${p.cardIndex}_${p.slot}`, p.blob, `photo-${p.slot}.jpg`));
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiUrl}/api/submissions/${encodeURIComponent(reference)}/photos`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setPhotoUploadState('uploading', Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if ((xhr.status >= 200 && xhr.status < 300) || xhr.status === 409) {
        setPhotoUploadState('done');
      } else if (attempt < 2) {
        uploadStagedPhotos(reference, staged, attempt + 1);
      } else {
        setPhotoUploadState('failed');
      }
    };
    xhr.onerror = () => {
      if (attempt < 2) uploadStagedPhotos(reference, staged, attempt + 1);
      else setPhotoUploadState('failed');
    };
    xhr.send(fd);
  }

  function setPhotoUploadState(status, pct = 100) {
    state.photoUpload = { status, pct };
    const el = root.querySelector('#csf-photo-upload');
    if (el) el.outerHTML = buildPhotoUploadCard();
  }

  async function loadCards() {
    state.loading = true;
    refreshGrid();
    const p = new URLSearchParams({ game: state.game, page: state.page, limit: PAGE_SIZE, sort: state.sort });
    if (state.q.length >= 2) p.set('q', state.q);
    if (state.filterSet)     p.set('set', state.filterSet);
    if (state.filterRarity)  p.set('rarity', state.filterRarity);
    try {
      const d = await apiFetch(`/api/cards?${p}`);
      state.cards = d.cards ?? [];
      state.total = d.total ?? 0;
      state.totalPages = d.totalPages ?? 1;
      state.availableSets = d.sets ?? [];
      state.availableRarities = d.rarities ?? [];
      state.pricesLastUpdated = d.pricesLastUpdated ?? null;
      state.loading = false;
      refreshControls();
      refreshGrid();
    } catch (e) {
      state.loading = false;
      state.cards = [];
      state.total = 0;
      state.totalPages = 1;
      refreshGrid();
      console.error('[CurioSell]', e.message);
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  const fmt = n => `£${Number(n).toFixed(2)}`;
  const esc = s => String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  const g   = () => GAMES[state.game];
  const basketTotal    = () => state.basket.reduce((s, i) => s + i.linePrice, 0);
  const basketCount    = () => state.basket.reduce((s, i) => s + i.quantity, 0);
  const effectiveTotal = () => {
    const base = basketTotal();
    return state.paymentMethod === 'store-credit'
      ? Math.round(base * STORE_CREDIT_UPLIFT * 100) / 100
      : base;
  };
  function postageFee() {
    return (POSTAGE.find(p => p.id === state.postageMethod) || {}).fee || 0;
  }
  function netPayout() {
    return Math.max(0, Math.round((effectiveTotal() - postageFee()) * 100) / 100);
  }

  // Downscale a photo before upload: 1600px long edge, JPEG q0.8. Condition photos
  // don't need 12MP — this turns ~4MB camera shots into ~300–500KB.
  function compressImage(file) {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width  = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        canvas.toBlob(b => resolve(b), 'image/jpeg', 0.8);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
      img.src = url;
    });
  }

  function stagedPhotoCount() {
    return Object.values(state.photos).reduce((n, arr) => n + arr.length, 0);
  }

  // Revoke thumbnail object URLs before dropping photo entries (bounded leak otherwise).
  function revokePhotoThumbs(entries) {
    (entries || []).forEach(p => { try { URL.revokeObjectURL(p.thumb); } catch { /* already gone */ } });
  }

  const condPrices  = card => {
    if (!card?.buyPrice) return null;
    const p = {};
    CONDITIONS.forEach(c => { p[c.id] = Math.round(card.buyPrice * c.mult * 100) / 100; });
    return p;
  };
  const formatDate = iso => iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/London' }) : '';

  const CHECK_SVG = `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;

  function trendFor(card) {
    const prices = (card.nmHistory || []).map(h => h.nmEu).filter(n => n > 0);
    if (prices.length < 2) return { dir: 'flat', pct: '0' };
    const win = prices.slice(-7);
    if (win.length < 2) return { dir: 'flat', pct: '0' };
    const raw = (win[win.length - 1] - win[0]) / win[0];
    if (Math.abs(raw) < 0.02) return { dir: 'flat', pct: '0' };
    return { dir: raw > 0 ? 'up' : 'down', pct: String(Math.abs(Math.round(raw * 100))) };
  }

  function buildTrendBadge(trend) {
    if (trend.dir === 'flat') return `<span class="csf-trend csf-trend--flat">—</span>`;
    return `<span class="csf-trend csf-trend--${trend.dir}">${trend.dir === 'up' ? '▲' : '▼'}${trend.pct}%</span>`;
  }

  function buildSparkline(card) {
    const prices = (card.nmHistory || []).map(h => h.nmEu).filter(n => n > 0);
    if (prices.length < 2) return '';
    const W = 56, H = 22;
    const min = Math.min(...prices), max = Math.max(...prices), rng = (max - min) || 1;
    const pctChange = (prices[prices.length - 1] - prices[0]) / prices[0];
    const col = Math.abs(pctChange) < 0.02 ? 'rgba(255,255,255,0.25)' : pctChange > 0 ? '#4ade80' : '#f87171';
    const coords = prices.map((p, i) =>
      `${((i / (prices.length - 1)) * W).toFixed(1)},${(H - ((p - min) / rng) * (H - 4) - 2).toFixed(1)}`
    ).join(' ');
    return `<svg class="csf-sparkline" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" aria-hidden="true" preserveAspectRatio="none"><polyline points="${coords}" fill="none" stroke="${col}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }

  // ── Zone refresh ──────────────────────────────────────────────────────────

  function refreshControls() {
    if (zones.controls) zones.controls.innerHTML = buildControls();
  }

  function refreshGrid() {
    if (zones.grid) zones.grid.innerHTML = state.loading ? buildSkeletons() : buildGrid();
  }

  function refreshBasket() {
    if (zones.basket)  zones.basket.innerHTML  = buildBasketAside();
    if (zones.fab)     zones.fab.innerHTML      = buildFab();
    if (zones.drawer)  zones.drawer.innerHTML   = buildDrawerContent();
  }

  function refreshPanel() {
    if (!zones.panel) return;
    if (state.panelOpen && state.selectedCard) {
      zones.panel.innerHTML = buildPanel();
      requestAnimationFrame(() => {
        zones.panel.classList.add('is-open');
        if (zones.panelBd) zones.panelBd.classList.add('is-open');
      });
    } else {
      zones.panel.classList.remove('is-open');
      if (zones.panelBd) zones.panelBd.classList.remove('is-open');
    }
  }

  function refreshDrawer() {
    if (!zones.drawer) return;
    refreshBasket();
    if (state.drawerOpen) {
      zones.drawer.classList.add('is-open');
      if (zones.drawerBd) zones.drawerBd.classList.add('is-open');
    } else {
      zones.drawer.classList.remove('is-open');
      if (zones.drawerBd) zones.drawerBd.classList.remove('is-open');
    }
  }

  function switchView(view) {
    state.view = view;
    if (zones.browse)   zones.browse.style.display   = view === 'browse'   ? '' : 'none';
    if (zones.checkout) {
      zones.checkout.style.display = view === 'checkout' ? '' : 'none';
      if (view === 'checkout') zones.checkout.innerHTML = buildCheckout();
    }
    if (zones.success)  {
      zones.success.style.display  = view === 'success'  ? '' : 'none';
      if (view === 'success')  zones.success.innerHTML  = buildSuccess();
    }
    if (zones.track) {
      zones.track.style.display = view === 'track' ? '' : 'none';
      if (view === 'track') zones.track.innerHTML = buildTrackView();
    }
    if (root) root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ── Shell ─────────────────────────────────────────────────────────────────

  function init() {
    root.innerHTML = `<style>${CSS}</style><div class="csf-root">${buildShell()}</div>`;
    zones = {
      browse:   root.querySelector('#csf-browse'),
      controls: root.querySelector('#csf-controls'),
      grid:     root.querySelector('#csf-grid'),
      basket:   root.querySelector('#csf-basket'),
      panel:    root.querySelector('#csf-panel'),
      panelBd:  root.querySelector('#csf-panel-bd'),
      drawer:   root.querySelector('#csf-drawer'),
      drawerBd: root.querySelector('#csf-drawer-bd'),
      fab:      root.querySelector('#csf-fab'),
      checkout: root.querySelector('#csf-checkout'),
      success:  root.querySelector('#csf-success'),
      track:    root.querySelector('#csf-track'),
    };
    bindEvents();
    refreshBasket();
    loadCards();
  }

  function buildShell() {
    return `
      <!-- Browse -->
      <div id="csf-browse">
        <div class="csf-hero">
          <div class="csf-hero-glow" aria-hidden="true"></div>
          <p class="csf-hero-eyebrow">We buy singles &amp; collections</p>
          <h1 class="csf-hero-title">Sell Your Cards</h1>
          <p class="csf-hero-sub">Browse our buy list, pick your cards, and we'll make you an offer — paid within 2 business days of receiving them.</p>
          <p class="csf-hero-eligibility">You must be 18 or over to sell to Curio Cards. Under 18? Please ask a parent or guardian to complete the sale on your behalf.</p>
        </div>
        <div class="csf-main">
          <div id="csf-controls" class="csf-controls"></div>
          <div class="csf-layout">
            <div class="csf-grid-col">
              <div id="csf-grid" class="csf-grid"></div>
            </div>
            <aside id="csf-basket" class="csf-basket-aside"></aside>
          </div>
        </div>
      </div>
      <!-- Checkout -->
      <div id="csf-checkout" style="display:none;"></div>
      <!-- Success -->
      <div id="csf-success" style="display:none;"></div>
      <!-- Submission tracker -->
      <div id="csf-track" style="display:none;"></div>
      <!-- Card detail panel -->
      <div id="csf-panel" class="csf-panel" role="dialog" aria-modal="true"></div>
      <div id="csf-panel-bd" class="csf-panel-bd" data-action="close-panel"></div>
      <!-- Mobile basket drawer -->
      <div id="csf-drawer" class="csf-drawer"></div>
      <div id="csf-drawer-bd" class="csf-drawer-bd" data-action="close-drawer"></div>
      <!-- Mobile FAB -->
      <div id="csf-fab" class="csf-fab"></div>
    `;
  }

  // ── Controls ──────────────────────────────────────────────────────────────

  function buildControls() {
    const game = g();
    return `
      <div class="csf-game-tabs-row">
        <div class="csf-game-tabs">
          ${Object.values(GAMES).map(gm => `
            <button class="csf-game-tile${gm.id === state.game ? ' is-active' : ''}"
                    data-action="set-game" data-game="${gm.id}"
                    style="--tile-color:${gm.color};--tile-rgb:${gm.rgb};">
              <div class="csf-game-tile-glow" aria-hidden="true"></div>
              <div class="csf-game-tile-shimmer" aria-hidden="true"></div>
              ${gm.logoUrl
                ? `<img class="csf-game-tile-logo" src="${esc(gm.logoUrl)}" alt="${esc(gm.label)}" loading="eager">`
                : `<span class="csf-game-tile-wordmark">${esc(gm.label)}</span>`}
              <span class="csf-game-tile-name">${esc(gm.label)}</span>
            </button>
          `).join('')}
        </div>
        <button class="csf-track-link" data-action="go-track" aria-label="Track a submission">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          Track a submission
        </button>
      </div>
      <div class="csf-filter-row">
        <div class="csf-search-wrap">
          <svg class="csf-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input class="csf-search-input" type="search" autocomplete="off" spellcheck="false"
                 placeholder="Search cards…" value="${esc(state.q)}"
                 data-action="search-input" aria-label="Search cards">
        </div>
        <select class="csf-select" data-action="set-filter-set" aria-label="Filter by set">
          <option value="">All Sets</option>
          ${state.availableSets.map(s => `<option value="${esc(s)}"${state.filterSet === s ? ' selected' : ''}>${esc(s)}</option>`).join('')}
        </select>
        <select class="csf-select" data-action="set-filter-rarity" aria-label="Filter by rarity">
          <option value="">All Rarities</option>
          ${state.availableRarities.map(r => `<option value="${esc(r)}"${state.filterRarity === r ? ' selected' : ''}>${esc(r)}</option>`).join('')}
        </select>
        <select class="csf-select" data-action="set-sort" aria-label="Sort by">
          ${SORTS.map(s => `<option value="${s.id}"${state.sort === s.id ? ' selected' : ''}>${s.label}</option>`).join('')}
        </select>
        ${state.total > 0 ? `<span class="csf-result-count">${state.total.toLocaleString()} cards</span>` : ''}
      </div>
    `;
  }

  // ── Card grid ─────────────────────────────────────────────────────────────

  function buildSkeletons() {
    return Array.from({ length: PAGE_SIZE }, (_, i) => `
      <div class="csf-card-skeleton" style="animation-delay:${i*0.04}s">
        <div class="csf-sk-img"></div>
        <div class="csf-sk-body">
          <div class="csf-sk-line" style="width:80%"></div>
          <div class="csf-sk-line" style="width:55%"></div>
          <div class="csf-sk-line" style="width:65%"></div>
        </div>
      </div>
    `).join('');
  }

  function buildGrid() {
    if (!state.cards.length) {
      return `
        <div class="csf-empty">
          <div class="csf-empty-icon">🔍</div>
          <p>No cards found${state.q ? ` for "${esc(state.q)}"` : ''}.</p>
          ${state.q || state.filterSet || state.filterRarity ? '<p style="font-size:13px;margin-top:8px;">Try clearing your filters.</p>' : ''}
        </div>
        ${buildPagination()}
      `;
    }
    const gameColor = g().color;
    return `
      ${state.cards.map((card, i) => buildCard(card, i, gameColor)).join('')}
      ${buildPagination()}
      ${state.pricesLastUpdated ? `<div class="csf-sync-bar">Prices last updated: ${formatDate(state.pricesLastUpdated)}</div>` : ''}
    `;
  }

  function buildCard(card, i, gameColor) {
    const prices = condPrices(card);
    return `
      <div class="csf-card" style="animation-delay:${Math.min(i,15)*0.03}s"
           data-action="open-panel" data-card-id="${esc(card.id)}">
        <div class="csf-card-img-wrap">
          ${card.imageUrl
            ? `<img class="csf-card-img" src="${esc(card.imageUrl)}" alt="${esc(card.name)}" loading="lazy">`
            : `<div class="csf-card-img-ph">🃏</div>`}
        </div>
        <div class="csf-card-line" style="background:${gameColor}"></div>
        <div class="csf-card-body">
          <h3 class="csf-card-name">${esc(card.name)}</h3>
          <p class="csf-card-set">${esc(card.set)}${card.number ? ` · ${esc(card.number)}` : ''}</p>
          <div class="csf-card-bottom">
            ${card.rarity ? `<div class="csf-card-badges"><span class="csf-badge">${esc(card.rarity)}</span></div>` : ''}
            ${prices ? `
              <div class="csf-card-nm-row">
                <div class="csf-card-nm-left">
                  <span class="csf-nm-label">NM</span>
                  <span class="csf-card-nm-price">${fmt(prices['NM'])}</span>
                </div>
                ${buildSparkline(card)}
              </div>
            ` : ''}
            <button class="csf-card-sell-btn" data-action="open-panel" data-card-id="${esc(card.id)}"
                    style="background:${gameColor};color:${g().text};">
              Sell this card
            </button>
          </div>
        </div>
      </div>
    `;
  }

  function buildPagination() {
    if (state.totalPages <= 1) return '';
    const { page, totalPages } = state;
    const pages = paginationRange(page, totalPages);
    return `
      <div class="csf-pagination">
        <button class="csf-page-btn" data-action="set-page" data-page="${page - 1}"
                ${page <= 1 ? 'disabled' : ''} aria-label="Previous">‹</button>
        ${pages.map(p => p === '…'
          ? `<span class="csf-page-ellipsis">…</span>`
          : `<button class="csf-page-btn${p === page ? ' is-active' : ''}"
                     data-action="set-page" data-page="${p}">${p}</button>`
        ).join('')}
        <button class="csf-page-btn" data-action="set-page" data-page="${page + 1}"
                ${page >= totalPages ? 'disabled' : ''} aria-label="Next">›</button>
      </div>
    `;
  }

  function paginationRange(cur, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    if (cur <= 4) return [1, 2, 3, 4, 5, '…', total];
    if (cur >= total - 3) return [1, '…', total-4, total-3, total-2, total-1, total];
    return [1, '…', cur-1, cur, cur+1, '…', total];
  }

  // ── Card detail panel ─────────────────────────────────────────────────────

  function buildPanel() {
    const card  = state.selectedCard;
    const nmPrice = card?.buyPrice ?? 0;
    const total   = Math.round(nmPrice * state.selectedQty * 100) / 100;
    const gm      = g();
    return `
      <div class="csf-panel-header">
        <span class="csf-panel-title">Sell this card</span>
        <button class="csf-panel-close" data-action="close-panel" aria-label="Close">✕</button>
      </div>
      <div class="csf-panel-body">
        <div class="csf-panel-card-row">
          ${card.imageUrl
            ? `<img class="csf-panel-img" src="${esc(card.imageUrl)}" alt="${esc(card.name)}">`
            : ''}
          <div class="csf-panel-card-info">
            <h2 class="csf-panel-card-name">${esc(card.name)}</h2>
            <p class="csf-panel-card-set">${esc(card.set)}${card.number ? ` · ${esc(card.number)}` : ''}</p>
            ${card.rarity ? `<span class="csf-badge">${esc(card.rarity)}</span>` : ''}
          </div>
        </div>

        <div class="csf-panel-nm-banner">
          <div class="csf-panel-nm-info">
            <span class="csf-panel-nm-tag">NM</span>
            <span class="csf-panel-nm-label">Near Mint only — cards must show no visible wear</span>
          </div>
          <div class="csf-panel-nm-spark">${buildSparkline(card)}</div>
        </div>

        <div class="csf-panel-section">
          <p class="csf-panel-label">Quantity</p>
          <div class="csf-qty-row">
            <button class="csf-qty-btn" data-action="qty-dec" aria-label="Decrease">−</button>
            <span class="csf-qty-val">${state.selectedQty}</span>
            <button class="csf-qty-btn" data-action="qty-inc" aria-label="Increase">+</button>
          </div>
        </div>
      </div>
      <div class="csf-panel-cta">
        <div class="csf-panel-price-row">
          <span class="csf-panel-price-label">Your offer (NM × ${state.selectedQty})</span>
          <span class="csf-panel-price-val">${fmt(total)}</span>
        </div>
        <button class="csf-btn-primary" data-action="add-to-basket"
                style="background:${gm.color};color:${gm.text};">
          Add to basket
        </button>
      </div>
    `;
  }

  // ── Basket ────────────────────────────────────────────────────────────────

  function buildBasketAside() {
    return buildBasketInner();
  }

  function buildFab() {
    const count = basketCount();
    return `
      <button class="csf-fab-btn${count > 0 ? '' : ' is-empty'}" data-action="open-drawer">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
        ${count > 0 ? `<span class="csf-fab-count">${count}</span>` : ''}
        ${count > 0 ? fmt(basketTotal()) : 'Basket'}
      </button>
    `;
  }

  function buildDrawerContent() {
    return `
      <div class="csf-drawer-top">
        <div class="csf-drawer-handle"></div>
      </div>
      <div class="csf-drawer-inner">
        ${buildBasketInner(true)}
      </div>
    `;
  }

  function buildBasketInner(isDrawer = false) {
    const total = basketTotal();
    const has   = state.basket.length > 0;
    return `
      <div class="csf-basket-head">
        <span class="csf-basket-title">Your Sell Basket</span>
        <div class="csf-basket-head-right">
          <span class="csf-basket-badge">${state.basket.length} ${state.basket.length === 1 ? 'card' : 'cards'}</span>
          ${isDrawer ? `<button class="csf-drawer-close-btn" data-action="close-drawer" aria-label="Close basket">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>` : ''}
        </div>
      </div>
      ${!has ? `
        <div class="csf-basket-empty">
          <div class="csf-basket-empty-icon" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 01-8 0"/>
            </svg>
          </div>
          <p class="csf-basket-empty-title">Your basket is empty</p>
          <p class="csf-basket-empty-sub">Browse the buy list above and tap <em>Sell this card</em> to add cards here.</p>
        </div>
      ` : `
        <div class="csf-basket-items">
          ${state.basket.map(item => `
            <div class="csf-basket-item">
              <div class="csf-basket-item-main">
                <div class="csf-basket-item-info">
                  <div class="csf-basket-item-name">${esc(item.name)}</div>
                  <div class="csf-basket-item-meta">${item.cond} · qty ${item.qty}${item.set ? ` · ${esc(item.set)}` : ''}</div>
                </div>
                <div class="csf-basket-item-right">
                  <span class="csf-basket-item-price">${fmt(item.linePrice)}</span>
                  <button class="csf-basket-remove" data-action="remove-item" data-key="${item.key}" aria-label="Remove">×</button>
                </div>
              </div>
              <div class="csf-photo-block">
                <button class="csf-photo-toggle${state.photoOpen[item.key] ? ' is-open' : ''}" data-action="toggle-photos" data-key="${item.key}" aria-expanded="${state.photoOpen[item.key] ? 'true' : 'false'}">
                  <svg class="csf-photo-toggle-ic" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                  <span>Add photos (optional)${(state.photos[item.key] || []).length ? ` · ${state.photos[item.key].length}/2` : ''}</span>
                </button>
                ${state.photoOpen[item.key] ? `
                  <p class="csf-photo-hint">Helps us verify condition faster — front &amp; back if you can.</p>
                  <div class="csf-photo-thumbs">
                    ${(state.photos[item.key] || []).map((p, pi) => `
                      <span class="csf-photo-thumb">
                        <img src="${p.thumb}" alt="Photo ${pi + 1}">
                        <button class="csf-photo-remove" data-action="remove-photo" data-key="${item.key}" data-pi="${pi}" aria-label="Remove photo">×</button>
                      </span>
                    `).join('')}
                    ${(state.photos[item.key] || []).length < 2 ? `
                      <label class="csf-photo-add" aria-label="Add a photo">
                        +
                        <input type="file" accept="image/jpeg,image/png,image/webp" multiple
                               data-action="photo-input" data-key="${item.key}" style="display:none;">
                      </label>
                    ` : ''}
                  </div>
                ` : ''}
              </div>
            </div>
          `).join('')}
        </div>
        <div class="csf-basket-foot">
          <div class="csf-basket-total-row">
            <span>Total offer</span><strong>${fmt(total)}</strong>
          </div>
          ${total > 0 && total < MIN_ORDER ? `
            <div class="csf-min-warn">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="m10.29 3.86-8.83 15.3A2 2 0 0 0 3.17 22h17.66a2 2 0 0 0 1.71-3.01L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <span>Add <strong>£${(MIN_ORDER - total).toFixed(2)}</strong> more to reach the £${MIN_ORDER} minimum</span>
            </div>
          ` : ''}
          <button class="csf-btn-primary" data-action="go-checkout" ${total > 0 && total < MIN_ORDER ? 'disabled' : ''}>
            Continue to checkout →
          </button>
        </div>
      `}
    `;
  }

  // ── Checkout ──────────────────────────────────────────────────────────────

  function buildCheckout() {
    const total = basketTotal();
    const gm    = g();
    const isPrepaid = state.postageMethod !== 'self-arranged';
    return `
      <div class="csf-checkout-wrap">
        <button class="csf-back-btn" data-action="go-browse">← Back to cards</button>
        <h2 class="csf-checkout-title">Your details</h2>
        <form id="csf-form">
          <div class="csf-form-group">
            <label class="csf-form-label" for="csf-name">Full name</label>
            <input class="csf-form-input" id="csf-name" name="customerName" type="text"
                   value="${esc(state.customerName)}" placeholder="First and last name" autocomplete="name" required>
          </div>
          <div class="csf-form-group">
            <label class="csf-form-label" for="csf-email">Email address</label>
            <input class="csf-form-input" id="csf-email" name="customerEmail" type="email"
                   value="${esc(state.customerEmail)}" placeholder="you@example.com" autocomplete="email" required>
          </div>
          <div class="csf-form-group">
            <label class="csf-form-label">How will you send your cards?</label>
            <div class="csf-postage-opts">
              ${POSTAGE.map(opt => `
                <label class="csf-postage-opt${state.postageMethod === opt.id ? ' is-sel' : ''}">
                  <input type="radio" name="postageMethod" value="${opt.id}"
                         ${state.postageMethod === opt.id ? 'checked' : ''}
                         data-action="set-postage" data-postage="${opt.id}">
                  <div>
                    <div class="csf-postage-opt-name">${opt.label}</div>
                    <div class="csf-postage-opt-desc">${opt.desc}</div>
                  </div>
                </label>
              `).join('')}
            </div>
          </div>
          ${isPrepaid ? `
          <div class="csf-form-group">
            <label class="csf-form-label" for="csf-addr1">Address line 1</label>
            <input class="csf-form-input" id="csf-addr1" name="address1" type="text"
                   value="${esc(state.address.address1)}" autocomplete="address-line1" required>
          </div>
          <div class="csf-form-group">
            <label class="csf-form-label" for="csf-addr2">Address line 2 <span style="color:rgba(255,255,255,.35);">(optional)</span></label>
            <input class="csf-form-input" id="csf-addr2" name="address2" type="text"
                   value="${esc(state.address.address2)}" autocomplete="address-line2">
          </div>
          <div class="csf-form-row" style="display:flex;gap:10px;">
            <div class="csf-form-group" style="flex:1;">
              <label class="csf-form-label" for="csf-city">Town/City</label>
              <input class="csf-form-input" id="csf-city" name="city" type="text"
                     value="${esc(state.address.city)}" autocomplete="address-level2" required>
            </div>
            <div class="csf-form-group" style="flex:1;">
              <label class="csf-form-label" for="csf-postcode">Postcode</label>
              <input class="csf-form-input" id="csf-postcode" name="postcode" type="text"
                     value="${esc(state.address.postcode)}" autocomplete="postal-code" required>
            </div>
          </div>
          <div class="csf-form-group">
            <label class="csf-form-label" for="csf-phone">Phone number</label>
            <input class="csf-form-input" id="csf-phone" name="phone" type="tel"
                   value="${esc(state.address.phone)}" autocomplete="tel" required>
          </div>
          ` : ''}
          <div class="csf-form-group">
            <label class="csf-form-label">How would you like to be paid?</label>
            <div class="csf-payment-opts">
              ${PAYMENT_METHODS.map(opt => `
                <label class="csf-payment-opt${state.paymentMethod === opt.id ? ' is-sel' : ''}${opt.id === 'store-credit' ? ' is-credit' : ''}">
                  <input type="radio" name="paymentMethod" value="${opt.id}"
                         ${state.paymentMethod === opt.id ? 'checked' : ''}
                         data-action="set-payment" data-payment="${opt.id}">
                  <div style="flex:1">
                    <div class="csf-payment-opt-name">${opt.label}${opt.badge ? ` <span class="csf-payment-badge">${opt.badge}</span>` : ''}</div>
                    <div class="csf-payment-opt-desc">${opt.desc}</div>
                  </div>
                </label>
                ${opt.id === 'store-credit' && state.paymentMethod === 'store-credit' ? `
                  <p class="csf-payment-gdpr">By selecting store credit, you agree to a Curio Cards account being created or updated with your email address to hold your credit. You can request deletion at any time by emailing <a href="mailto:hello@curiocards.co.uk" style="color:#888;text-decoration:underline;">hello@curiocards.co.uk</a></p>
                ` : ''}
                ${opt.id === 'paypal' && state.paymentMethod === 'paypal' ? `
                  <div class="csf-paypal-block">
                    <label class="csf-paypal-same">
                      <input type="checkbox" data-action="toggle-paypal-same" ${state.paypalSameAsContact ? 'checked' : ''}>
                      <span>Send payment to my contact email</span>
                    </label>
                    ${!state.paypalSameAsContact ? `
                      <div class="csf-form-group" style="margin:8px 0 0;">
                        <label class="csf-form-label" for="csf-paypal-email">PayPal email</label>
                        <input class="csf-form-input" id="csf-paypal-email" name="paypalEmail" type="email"
                               value="${esc(state.paypalEmail)}" placeholder="you@example.com" autocomplete="email">
                      </div>
                    ` : ''}
                  </div>
                ` : ''}
              `).join('')}
            </div>
          </div>
          <div class="csf-order-summary">
            <div class="csf-order-summary-title">Order summary</div>
            ${state.basket.map(item => `
              <div class="csf-order-row">
                <span class="csf-order-name">${esc(item.name)} (${item.cond} ×${item.qty})</span>
                <span>${fmt(item.linePrice)}</span>
              </div>
            `).join('')}
            <div class="csf-order-divider"></div>
            ${state.paymentMethod === 'store-credit' ? `
              <div class="csf-order-row" style="color:rgba(255,255,255,.35);font-size:13px;">
                <span>Base offer (80%)</span><span style="text-decoration:line-through;">${fmt(total)}</span>
              </div>
              <div class="csf-order-row" style="color:#4ade80;font-size:12px;padding:2px 0;">
                <span>Store credit bonus (+10%)</span><span>+${fmt(Math.round((effectiveTotal() - total) * 100) / 100)}</span>
              </div>
            ` : ''}
            ${state.postageMethod === 'self-arranged' ? `
            <div class="csf-order-row" style="color:rgba(255,255,255,.55);font-size:13px;">
              <span>Postage</span><span>You arrange your own</span>
            </div>` : `
            <div class="csf-order-row" style="color:rgba(255,255,255,.55);font-size:13px;">
              <span>Postage (${esc((POSTAGE.find(p => p.id === state.postageMethod) || {}).label || '')})</span>
              <span>−${fmt(postageFee())}</span>
            </div>`}
            ${state.paymentMethod === 'paypal' ? `
            <div class="csf-order-row" style="color:rgba(255,255,255,.55);font-size:13px;">
              <span>Paid via PayPal to</span>
              <span>${state.paypalSameAsContact ? 'your contact email' : esc(state.paypalEmail || '—')}</span>
            </div>` : ''}
            <div class="csf-order-row csf-order-total"${state.paymentMethod === 'store-credit' ? ' style="color:var(--accent);"' : ''}>
              <span>Net payout</span><span>${fmt(netPayout())}</span>
            </div>
          </div>
          <label class="csf-age-confirm">
            <input type="checkbox" name="ageConfirmed" data-action="toggle-age" ${state.ageConfirmed ? 'checked' : ''}>
            <span>I confirm I am 18 years of age or over.</span>
          </label>
          <p class="csf-age-terms">By submitting, you agree to our <a href="/policies/terms-of-service" target="_blank" rel="noopener">Selling Terms</a>.</p>
          ${state.submitError ? `<div class="csf-error-msg">${esc(state.submitError)}</div>` : ''}
          <button class="csf-btn-primary" type="submit"
                  style="background:${gm.color};color:${gm.text};"
                  ${state.submitting || !state.ageConfirmed ? 'disabled' : ''}>
            ${state.submitting ? '<span class="csf-spin"></span> Submitting…' : 'Submit sell request'}
          </button>
          ${!state.ageConfirmed ? `<p class="csf-age-hint">Please confirm you are 18 or over to submit.</p>` : ''}
        </form>
      </div>
    `;
  }

  function buildPhotoUploadCard() {
    const u = state.photoUpload;
    if (!u) return '';
    const inner = u.status === 'uploading' ? `
        <p style="margin:0 0 8px;font-weight:600;">✅ Your sale is confirmed. Please don't close or refresh this page while we upload your condition photos…</p>
        <div style="height:6px;border-radius:3px;background:rgba(255,255,255,.08);overflow:hidden;">
          <div style="height:100%;width:${u.pct}%;background:var(--accent);transition:width .3s;"></div>
        </div>`
      : u.status === 'done' ? `
        <p style="margin:0;font-weight:600;color:#4ade80;">✓ Photos attached — thanks, that helps us grade faster.</p>`
      : `
        <p style="margin:0;color:#FFC107;">We couldn't upload some photos — no problem, your sale is unaffected.</p>`;
    return `<div id="csf-photo-upload" style="background:rgba(255,255,255,.04);border-radius:10px;padding:14px 16px;margin:0 auto 14px;max-width:420px;text-align:left;">${inner}</div>`;
  }

  function buildSuccess() {
    const lbl = state.successLabel || {};
    const pt = state.successPostTo;
    const selfBlock = (lbl.labelStatus === 'self-arranged' && pt) ? `
        <div style="background:rgba(255,255,255,.04);border-radius:10px;padding:14px 16px;margin:0 auto 12px;max-width:360px;text-align:left;">
          <p style="margin:0 0 6px;font-weight:600;">Post your cards (tracked) to:</p>
          <p style="margin:0;font-size:14px;line-height:1.5;">${esc(pt.name)}<br>${esc(pt.address_line1)}<br>${pt.city_locality ? esc(pt.city_locality) + '<br>' : ''}${esc(pt.postal_code)}</p>
        </div>
        <p style="color:var(--accent);font-size:14px;max-width:360px;margin:0 auto 12px;text-align:left;"><strong>Important:</strong> write your reference <strong>${esc(state.successRef)}</strong> on a slip inside your parcel so we can match it to your offer and pay you.</p>
        <p class="csf-success-body" style="margin-top:0;">We've emailed you these details too. You keep the full offer — no postage deducted.</p>
      ` : '';
    const labelBlock = lbl.labelStatus === 'self-arranged'
      ? selfBlock
      : (lbl.labelStatus === 'created' && lbl.labelPdfUrl ? `
        <a class="csf-btn-primary" href="${esc(lbl.labelPdfUrl)}" target="_blank" rel="noopener"
           style="display:inline-block;text-decoration:none;margin-bottom:10px;">Download your shipping label (PDF)</a>
        <p class="csf-success-body" style="margin-top:0;">Tracking: <strong>${esc(lbl.trackingNumber || '')}</strong>${lbl.trackingNumber ? ` · <a href="${royalMailTrackingUrl(lbl.trackingNumber)}" target="_blank" rel="noopener" style="color:var(--accent);">Track your parcel →</a>` : ''} · we've emailed the label to you too.</p>
        ${lbl.postByDate ? `<p class="csf-success-body" style="margin-top:0;color:var(--accent);font-weight:600;">Please post by ${esc(lbl.postByDate)}.</p>` : ''}
      ` : `
        <p class="csf-success-body">We're preparing your Royal Mail label and will email it to you shortly.</p>
      `);
    return `
      <div class="csf-success-wrap">
        <div class="csf-success-icon">✓</div>
        <h2 class="csf-success-title">Request received!</h2>
        <p class="csf-success-ref">${esc(state.successRef)}</p>
        ${buildPhotoUploadCard()}
        ${state.successPayout ? `<p class="csf-success-body" style="margin-top:0;">We'll send your payment to <strong>${esc(state.successPayout)}</strong> once your cards are graded.</p>` : ''}
        ${labelBlock}
        <p class="csf-success-body">
          Pack your cards securely (sleeves + a rigid toploader). We'll process your payment
          within 2 business days of receiving them.
        </p>
        <div style="display:flex;gap:10px;justify-content:center;flex-wrap:wrap;">
          <button class="csf-btn-ghost" data-action="track-this" data-ref="${esc(state.successRef)}">Track this submission</button>
          <button class="csf-btn-ghost" data-action="start-over">Submit another request</button>
        </div>
      </div>
    `;
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  function openPanel(cardId) {
    const card = state.cards.find(c => c.id === cardId);
    if (!card) return;
    state.selectedCard = card;
    state.selectedCond = 'NM';
    state.selectedQty  = 1;
    state.panelOpen    = true;
    refreshPanel();
    document.body.style.overflow = 'hidden';
  }

  function closePanel() {
    state.panelOpen = false;
    refreshPanel();
    setTimeout(() => {
      if (!state.panelOpen) state.selectedCard = null;
    }, 300);
    document.body.style.overflow = '';
  }

  function addToBasket() {
    const card  = state.selectedCard;
    if (!card) return;
    const price = card.buyPrice ?? 0;
    const key   = `${card.id}-NM`;
    const existing = state.basket.find(i => i.key === key);
    if (existing) {
      existing.qty += state.selectedQty;
      existing.linePrice = Math.round(existing.unitPrice * existing.qty * 100) / 100;
    } else {
      state.basket.push({
        key, cardId: card.id, game: state.game,
        name: card.name, set: card.set, number: card.number,
        cond: 'NM', qty: state.selectedQty,
        unitPrice: price, linePrice: Math.round(price * state.selectedQty * 100) / 100,
      });
    }
    closePanel();
    refreshBasket();
  }

  // ── Submission tracker ─────────────────────────────────────────────────────

  const TRACK_STAGES = [
    { id: 'received', label: 'Received',     desc: 'Your sell request has been logged and is awaiting our team.' },
    { id: 'grading',  label: 'Being Graded', desc: "Our team is inspecting and grading your cards." },
    { id: 'paid',     label: 'Payment Sent', desc: 'Your payment has been sent — check your email for details.' },
  ];

  function buildTrackView() {
    const t = state.tracker;
    const stageIdx = Math.max(0, TRACK_STAGES.findIndex(s => s.id === (t.found?.status ?? 'received')));
    return `
      <div class="csf-track-wrap">
        <button class="csf-back-btn" data-action="go-browse">← Back to buy list</button>
        <h2 class="csf-checkout-title">Track your submission</h2>
        <p class="csf-track-sub">Enter your reference number to check the status of your sell request.</p>
        <form id="csf-track-form" class="csf-track-form">
          <div class="csf-track-input-row">
            <input class="csf-form-input csf-track-input" type="text" id="csf-track-ref"
                   placeholder="e.g. CC-20250512-A4F2B1"
                   value="${esc(t.ref)}"
                   data-action="track-input" autocomplete="off" spellcheck="false">
            <button class="csf-btn-primary csf-track-submit-btn" type="submit"
                    ${t.loading ? 'disabled' : ''}>
              ${t.loading ? '<span class="csf-spin"></span>' : 'Check'}
            </button>
          </div>
          ${t.error ? `<p class="csf-track-error">${esc(t.error)}</p>` : ''}
        </form>
        ${t.found ? `
          <div class="csf-tracker-result">
            <div class="csf-tracker-ref">${esc(t.found.reference)}</div>
            <div class="csf-tracker-steps">
              ${TRACK_STAGES.map((s, i) => {
                const isPaid = t.found?.status === 'paid';
                const isFinal = isPaid && i === stageIdx;
                const isDone = i < stageIdx || isFinal;
                const isCurrent = i === stageIdx && !isFinal;
                return `
                <div class="csf-step${isDone ? ' is-done' : ''}${isCurrent ? ' is-current' : ''}">
                  <div class="csf-step-left">
                    <div class="csf-step-dot">
                      ${isDone ? CHECK_SVG : isCurrent ? '<div class="csf-step-pulse"></div>' : ''}
                    </div>
                    ${i < TRACK_STAGES.length - 1 ? '<div class="csf-step-line"></div>' : ''}
                  </div>
                  <div class="csf-step-body">
                    <div class="csf-step-label">${esc(s.label)}</div>
                    ${i === stageIdx ? `<div class="csf-step-desc">${esc(s.desc)}</div>` : ''}
                  </div>
                </div>`;
              }).join('')}
            </div>
            ${t.found.submittedAt ? `<p class="csf-tracker-meta">Submitted ${formatDate(t.found.submittedAt)}</p>` : ''}
            ${(t.found.netPayout ?? t.found.totalOffer) ? `<p class="csf-tracker-meta">Net payout: <strong style="color:var(--accent)">${fmt(t.found.netPayout ?? t.found.totalOffer)}</strong></p>` : ''}
            ${t.found.trackingNumber ? `<p class="csf-tracker-meta">Parcel: ${esc(t.found.trackingNumber)} · <a href="${t.found.trackingUrl || royalMailTrackingUrl(t.found.trackingNumber)}" target="_blank" rel="noopener" style="color:var(--accent);">Track your parcel →</a></p>` : ''}
          </div>
        ` : ''}
        ${t.found === false ? `
          <div class="csf-tracker-notfound">
            <div class="csf-tracker-notfound-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </div>
            <p class="csf-tracker-notfound-title">Submission not found</p>
            <p class="csf-tracker-notfound-sub">No submission found for <strong>${esc(t.ref)}</strong>. Check your reference number — it will look like <code>CC-20250512-A4F2B1</code>.</p>
          </div>
        ` : ''}
      </div>
    `;
  }

  async function handleTrackSubmit(e) {
    e.preventDefault();
    const ref = state.tracker.ref.trim().toUpperCase();
    if (!ref) return;
    state.tracker = { ...state.tracker, loading: true, error: null, found: null };
    if (zones.track) zones.track.innerHTML = buildTrackView();
    try {
      const data = await apiFetch(`/api/submission/${encodeURIComponent(ref)}`);
      state.tracker.found = data;
    } catch (err) {
      if (err.message === 'API 404') {
        state.tracker.found = false;
      } else {
        state.tracker.error = 'Could not reach server. Please try again.';
      }
    }
    state.tracker.loading = false;
    if (zones.track) zones.track.innerHTML = buildTrackView();
  }

  // Normalise a UK postcode to canonical form ('sw1a1aa' → 'SW1A 1AA'); null if invalid.
  function normalizePostcode(raw) {
    const m = String(raw || '').toUpperCase().replace(/\s+/g, '').match(/^([A-Z]{1,2}\d[A-Z\d]?)(\d[A-Z]{2})$/);
    return m ? m[1] + ' ' + m[2] : null;
  }

  // A neutral, non-personal example postcode for validation hints (randomised).
  function examplePostcode() {
    const ex = ['SW1A 1AA', 'EC1A 1BB', 'W1A 0AX', 'M1 1AE', 'B33 8TH', 'CR2 6XH', 'L1 8JQ', 'EH1 1RE'];
    return ex[Math.floor(Math.random() * ex.length)];
  }

  function royalMailTrackingUrl(t) {
    return 'https://www.royalmail.com/track-your-item#/tracking-results/' + encodeURIComponent(t);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const f = e.target;
    const isPrepaid = state.postageMethod !== 'self-arranged';
    const name  = f.querySelector('[name="customerName"]').value.trim();
    const email = f.querySelector('[name="customerEmail"]').value.trim();
    const ageConfirmed = !!f.querySelector('[name="ageConfirmed"]')?.checked;
    state.ageConfirmed = ageConfirmed; // preserve the tick across any validation re-render
    let address = {};
    if (isPrepaid) {
      // Address inputs only exist in the DOM for prepaid postage.
      address = {
        address1: f.querySelector('[name="address1"]').value.trim(),
        address2: f.querySelector('[name="address2"]').value.trim(),
        city:     f.querySelector('[name="city"]').value.trim(),
        postcode: f.querySelector('[name="postcode"]').value.trim(),
        phone:    f.querySelector('[name="phone"]').value.trim(),
      };
      // Keep what they typed so a validation error never clears their entries.
      state.address = address;
    }
    state.customerName = name; state.customerEmail = email;
    const fail = (msg) => { state.submitError = msg; zones.checkout.innerHTML = buildCheckout(); };
    if (!name)  { fail('Please enter your full name.'); return; }
    if (name.split(/\s+/).filter(Boolean).length < 2) { fail('Please enter your full name (first and last).'); return; }
    if (!email) { fail('Please enter your email address.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { fail('Please enter a valid email address.'); return; }
    let paypalEmail = '';
    if (state.paymentMethod === 'paypal' && !state.paypalSameAsContact) {
      paypalEmail = (f.querySelector('[name="paypalEmail"]')?.value || '').trim();
      if (!paypalEmail) { fail('Please enter your PayPal email (or tick "Send payment to my contact email").'); return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(paypalEmail)) { fail('Please enter a valid PayPal email address.'); return; }
      state.paypalEmail = paypalEmail;
    }
    if (isPrepaid) {
      if (!address.address1 || !address.city || !address.postcode || !address.phone) {
        fail('Please fill in your full postal address and phone number.'); return;
      }
      const pc = normalizePostcode(address.postcode);
      if (!pc) { fail('Please enter a valid UK postcode (e.g. ' + examplePostcode() + ').'); return; }
      address.postcode = pc;          // send the canonical, spaced form
      state.address = address;        // reflect the formatted postcode back in the field
    }
    if (!ageConfirmed) { fail('Please confirm you are 18 or over to sell.'); return; }
    state.submitting = true; state.submitError = null;
    zones.checkout.innerHTML = buildCheckout();
    try {
      const data = await apiPost('/api/submit', {
        customerName: name, customerEmail: email,
        ...(isPrepaid ? { address } : {}),
        postageMethod: state.postageMethod,
        paymentMethod: state.paymentMethod,
        ...(state.paymentMethod === 'paypal' && paypalEmail ? { paypalEmail } : {}),
        ageConfirmed: true,
        items: state.basket.map(i => ({ cardId: i.cardId, game: i.game, condition: i.cond, quantity: i.qty })),
        photoCount: stagedPhotoCount(),
      });
      if (data.success) {
        state.successRef = data.reference;
        state.successPayout = state.paymentMethod === 'paypal' ? (paypalEmail || email) : null;
        state.successLabel = { labelStatus: data.labelStatus, trackingNumber: data.trackingNumber, labelPdfUrl: data.labelPdfUrl, postByDate: data.postByDate };
        state.successPostTo = data.postTo || null;
        const staged = [];
        state.basket.forEach((item, cardIndex) => {
          (state.photos[item.key] || []).forEach((p, slot) => {
            staged.push({ cardIndex, slot: slot + 1, blob: p.blob });
          });
        });
        // Thumbs are display-only — revoke now; the blobs stay alive for the upload.
        Object.values(state.photos).forEach(revokePhotoThumbs);
        state.photos = {}; state.photoOpen = {};
        state.photoUpload = staged.length ? { status: 'uploading', pct: 0 } : null;
        state.basket = [];
        switchView('success');
        refreshBasket();
        if (staged.length) uploadStagedPhotos(data.reference, staged);
      } else {
        const detail = data.details && data.details[0];
        state.submitError = (detail && (detail.message || String(detail))) || data.error || 'Something went wrong. Please try again.';
        state.submitting = false;
        zones.checkout.innerHTML = buildCheckout();
      }
    } catch {
      state.submitError = 'Could not reach server. Please try again.';
      state.submitting = false;
      zones.checkout.innerHTML = buildCheckout();
    }
  }

  // ── Events ────────────────────────────────────────────────────────────────

  function bindEvents() {
    root.addEventListener('click',  handleClick);
    root.addEventListener('change', handleChange);
    root.addEventListener('input',  handleInput);
    root.addEventListener('submit', handleFormSubmit);
  }

  function handleClick(e) {
    const el = e.target.closest('[data-action]');
    if (!el) return;
    const { action } = el.dataset;

    // The file input lives inside its <label>; clicking the label fires a
    // synthetic click on this input. Bail before stopPropagation so the native
    // label→input picker opens untouched (the upload is handled in handleChange).
    if (action === 'photo-input') return;

    e.stopPropagation();

    switch (action) {
      case 'set-game':
        state.game = el.dataset.game; state.page = 1;
        state.filterSet = ''; state.filterRarity = '';
        refreshControls(); loadCards(); break;

      case 'open-panel':
        openPanel(el.dataset.cardId); break;

      case 'close-panel':
        closePanel(); break;

      case 'set-cond':
        state.selectedCond = el.dataset.cond;
        if (zones.panel) zones.panel.innerHTML = buildPanel(); break;

      case 'qty-inc':
        state.selectedQty = Math.min(10, state.selectedQty + 1);
        if (zones.panel) zones.panel.innerHTML = buildPanel(); break;

      case 'qty-dec':
        state.selectedQty = Math.max(1, state.selectedQty - 1);
        if (zones.panel) zones.panel.innerHTML = buildPanel(); break;

      case 'add-to-basket':
        addToBasket(); break;

      case 'remove-item': {
        const key = el.dataset.key;
        state.basket = state.basket.filter(i => i.key !== key);
        revokePhotoThumbs(state.photos[key]);
        delete state.photos[key];
        delete state.photoOpen[key];
        refreshBasket(); break;
      }

      case 'toggle-photos':
        state.photoOpen[el.dataset.key] = !state.photoOpen[el.dataset.key];
        refreshBasket();
        break;

      case 'remove-photo': {
        const arr = state.photos[el.dataset.key] || [];
        revokePhotoThumbs(arr.splice(parseInt(el.dataset.pi, 10), 1));
        if (!arr.length) delete state.photos[el.dataset.key];
        refreshBasket();
        break;
      }

      case 'open-drawer':
        state.drawerOpen = true; refreshDrawer();
        document.body.style.overflow = 'hidden'; break;

      case 'close-drawer':
        state.drawerOpen = false; refreshDrawer();
        document.body.style.overflow = ''; break;

      case 'go-checkout':
        state.drawerOpen = false;
        if (zones.drawerBd) zones.drawerBd.classList.remove('is-open');
        if (zones.drawer)   zones.drawer.classList.remove('is-open');
        document.body.style.overflow = '';
        switchView('checkout'); break;

      case 'go-browse':
        switchView('browse'); break;

      case 'set-page':
        state.page = parseInt(el.dataset.page, 10);
        loadCards();
        if (zones.grid) zones.grid.scrollIntoView({ behavior: 'smooth', block: 'start' }); break;

      case 'start-over':
        state.view = 'browse'; state.successRef = null; state.successLabel = null; state.successPostTo = null; state.ageConfirmed = false;
        state.photoUpload = null;
        state.successPayout = null;
        switchView('browse'); break;

      case 'go-track':
        switchView('track'); break;

      case 'track-this':
        state.tracker = { ref: el.dataset.ref || '', found: null, loading: false, error: null };
        switchView('track'); break;

      case 'set-postage':
        state.postageMethod = el.dataset.postage;
        zones.checkout.innerHTML = buildCheckout(); break;

      case 'set-payment':
        state.paymentMethod = el.dataset.payment;
        zones.checkout.innerHTML = buildCheckout(); break;
    }
  }

  function handleChange(e) {
    if (e.target.dataset.action === 'photo-input') {
      const key = e.target.dataset.key;
      const files = Array.from(e.target.files || []).slice(0, 2);
      Promise.all(files.map(async (f) => {
        const blob = await compressImage(f);
        if (!blob || blob.size > 5 * 1024 * 1024) return null;  // unreadable or still >5MB
        return { blob, thumb: URL.createObjectURL(blob) };
      })).then(results => {
        const arr = state.photos[key] || [];
        for (const r of results) {
          if (r && arr.length < 2) arr.push(r);
        }
        if (arr.length) { state.photos[key] = arr; state.photoOpen[key] = true; }
        refreshBasket();
      });
      return;
    }

    const el = e.target.closest('[data-action]') || e.target;
    const action = el.dataset.action;
    if (!action) return;

    if (action === 'set-filter-set')    { state.filterSet    = el.value; state.page = 1; loadCards(); }
    if (action === 'set-filter-rarity') { state.filterRarity = el.value; state.page = 1; loadCards(); }
    if (action === 'set-sort')          { state.sort = el.value; state.page = 1; loadCards(); }
    if (action === 'set-postage')       { state.postageMethod = el.dataset.postage || el.value; zones.checkout.innerHTML = buildCheckout(); }
    if (action === 'set-payment')       { state.paymentMethod = el.dataset.payment  || el.value; zones.checkout.innerHTML = buildCheckout(); }
    // Re-render so the submit button enables/disables with the confirmation.
    if (action === 'toggle-age')        { state.ageConfirmed = el.checked; zones.checkout.innerHTML = buildCheckout(); }
    if (action === 'toggle-paypal-same') {
      state.paypalSameAsContact = el.checked;
      zones.checkout.innerHTML = buildCheckout();
      return;
    }
  }

  function handleInput(e) {
    if (e.target.dataset.action === 'search-input') {
      state.q = e.target.value;
      clearTimeout(searchTimer);
      if (state.q.length === 0 || state.q.length >= 2) {
        searchTimer = setTimeout(() => { state.page = 1; loadCards(); }, DEBOUNCE);
      }
    }
    if (e.target.dataset.action === 'track-input') {
      state.tracker.ref = e.target.value;
    }
    if (['address1','address2','city','postcode','phone'].includes(e.target.name)) {
      state.address[e.target.name] = e.target.value;
    }
    if (e.target.name === 'paypalEmail') state.paypalEmail = e.target.value;
    if (e.target.name === 'customerName')  { state.customerName  = e.target.value; }
    if (e.target.name === 'customerEmail') { state.customerEmail = e.target.value; }
  }

  function handleFormSubmit(e) {
    if (e.target.id === 'csf-form')       handleSubmit(e);
    if (e.target.id === 'csf-track-form') handleTrackSubmit(e);
  }

  // ── Util ──────────────────────────────────────────────────────────────────

  const esc2 = s => String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

  // ── CSS ───────────────────────────────────────────────────────────────────

  const CSS = `
    .csf-root *, .csf-root *::before, .csf-root *::after { box-sizing: border-box; margin: 0; padding: 0; }

    .csf-root {
      --accent: #FFC107;
      --accent-rgb: 255,193,7;
      --bg: #1f1f21;
      --card-bg: #242428;
      --text: #f2f2f2;
      --muted: rgba(242,242,242,0.4);
      --border: rgba(255,255,255,0.06);
      --radius: 12px;
      background: var(--bg);
      color: var(--text);
      font-family: var(--font-body-family, system-ui, sans-serif);
      position: relative;
      overflow-x: hidden;
    }

    /* Hero */
    .csf-hero {
      position: relative; overflow: hidden;
      padding: 52px 24px 44px; text-align: center;
    }
    .csf-hero-glow {
      position: absolute; inset: 0;
      background: radial-gradient(ellipse 70% 55% at 50% -5%, rgba(255,193,7,.13) 0%, transparent 70%);
      pointer-events: none;
    }
    .csf-hero-eyebrow {
      font-size: 11px; font-weight: 700; letter-spacing: .18em;
      text-transform: uppercase; color: rgba(255,193,7,.55);
      margin-bottom: 12px; position: relative; z-index: 1;
    }
    .csf-hero-title {
      font-size: clamp(2rem,5vw,3.2rem); font-weight: 900;
      letter-spacing: -.02em; color: #fff; line-height: 1;
      margin-bottom: 14px; position: relative; z-index: 1;
    }
    .csf-hero-sub {
      font-size: 15px; color: rgba(242,242,242,.48);
      max-width: 520px; margin: 0 auto; line-height: 1.65;
      position: relative; z-index: 1;
    }
    .csf-hero-eligibility {
      font-size: 13px; color: rgba(242,242,242,.38);
      max-width: 520px; margin: 12px auto 0; line-height: 1.6;
      position: relative; z-index: 1;
    }

    /* Main */
    .csf-main { max-width: 1400px; margin: 0 auto; padding: 0 20px 80px; }

    /* Controls */
    .csf-controls { margin-bottom: 24px; }

    .csf-game-tile {
      position: relative; overflow: hidden; isolation: isolate;
      display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px;
      min-width: 148px; height: 72px; padding: 10px 24px 14px;
      background: #242428; border-radius: 16px;
      border: 1px solid rgba(255,255,255,.05);
      cursor: pointer; font-family: inherit; user-select: none;
      --tile-color: #FFC107; --tile-rgb: 255,193,7;
      transition: transform .3s cubic-bezier(.34,1.56,.64,1), box-shadow .3s ease, border-color .3s ease;
    }
    .csf-game-tile:hover {
      transform: translateY(-4px) scale(1.02);
      box-shadow: 0 12px 32px rgba(var(--tile-rgb),.22), 0 4px 12px rgba(0,0,0,.55);
      border-color: rgba(var(--tile-rgb),.22);
    }
    .csf-game-tile.is-active {
      border-color: rgba(var(--tile-rgb),.35);
      box-shadow: 0 0 0 1px rgba(var(--tile-rgb),.12) inset, 0 8px 24px rgba(var(--tile-rgb),.18);
    }

    /* glow layer */
    .csf-game-tile-glow {
      position: absolute; inset: 0; border-radius: inherit; pointer-events: none; z-index: 0;
      opacity: 0; transition: opacity .3s ease;
      background: radial-gradient(ellipse at 50% 110%, rgba(var(--tile-rgb),.18) 0%, transparent 65%);
    }
    .csf-game-tile:hover .csf-game-tile-glow,
    .csf-game-tile.is-active .csf-game-tile-glow { opacity: 1; }

    /* shimmer sweep */
    .csf-game-tile-shimmer {
      position: absolute; top: 0; bottom: 0; left: 0; width: 40%;
      background: linear-gradient(105deg, transparent 20%, rgba(255,255,255,.07) 50%, transparent 80%);
      pointer-events: none; opacity: 0; z-index: 0;
    }
    .csf-game-tile:hover .csf-game-tile-shimmer {
      opacity: 1; animation: csf-tile-shimmer .6s ease forwards;
    }
    @keyframes csf-tile-shimmer {
      0%   { transform: translateX(-180%) skewX(-12deg); }
      100% { transform: translateX(280%)  skewX(-12deg); }
    }

    /* accent bar */
    .csf-game-tile::after {
      content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 2.5px;
      border-radius: 0 0 16px 16px;
      background: linear-gradient(90deg, rgb(var(--tile-rgb)), rgba(var(--tile-rgb),.5), rgb(var(--tile-rgb)));
      background-size: 200%; animation: csf-bar-shift 2.5s ease infinite;
      opacity: .55; transition: height .25s, opacity .25s; pointer-events: none;
    }
    .csf-game-tile:hover::after,
    .csf-game-tile.is-active::after { height: 3.5px; opacity: 1; }
    @keyframes csf-bar-shift {
      0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; }
    }

    .csf-game-tile-logo {
      max-width: 88px; max-height: 28px; width: auto; height: auto;
      object-fit: contain; display: block; position: relative; z-index: 1;
      transition: transform .28s ease;
    }
    .csf-game-tile:hover .csf-game-tile-logo { transform: scale(1.07); }

    .csf-game-tile-wordmark {
      font-size: 14px; font-weight: 800; letter-spacing: .02em;
      color: rgba(255,255,255,.75); position: relative; z-index: 1;
      transition: color .25s;
    }
    .csf-game-tile:hover .csf-game-tile-wordmark,
    .csf-game-tile.is-active .csf-game-tile-wordmark { color: rgb(var(--tile-rgb)); }

    .csf-game-tile-name {
      font-size: 9px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase;
      color: rgba(255,255,255,.22); position: relative; z-index: 1;
      transition: color .25s; line-height: 1;
    }
    .csf-game-tile:hover .csf-game-tile-name,
    .csf-game-tile.is-active .csf-game-tile-name { color: rgba(var(--tile-rgb), .7); }

    .csf-filter-row {
      display: flex; gap: 10px; align-items: center; flex-wrap: wrap;
    }
    .csf-search-wrap { position: relative; flex: 1; min-width: 180px; }
    .csf-search-icon {
      position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
      color: rgba(255,255,255,.3); pointer-events: none; z-index: 1;
    }
    .csf-search-input {
      width: 100% !important; padding: 0 14px 0 42px !important; height: 44px !important;
      border-radius: 10px !important; border: 1.5px solid rgba(255,255,255,.08) !important;
      background: rgba(255,255,255,.04) !important; color: var(--text) !important;
      font-size: 14px !important; font-family: inherit !important;
      outline: none !important; box-shadow: none !important; -webkit-appearance: none;
      transition: border-color .2s, box-shadow .2s !important;
    }
    .csf-search-input::placeholder { color: rgba(255,255,255,.28) !important; }
    .csf-search-input:focus {
      border-color: rgba(255,193,7,.4) !important;
      box-shadow: 0 0 0 3px rgba(255,193,7,.08) !important;
    }
    .csf-select {
      height: 44px; padding: 0 34px 0 12px; border-radius: 10px;
      border: 1.5px solid rgba(255,255,255,.08);
      background: rgba(255,255,255,.04); color: var(--text);
      font-size: 13px; font-family: inherit; outline: none; cursor: pointer;
      appearance: none; -webkit-appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
      background-repeat: no-repeat; background-position: right 10px center;
      min-width: 120px; white-space: nowrap;
      transition: border-color .15s;
    }
    .csf-select:focus { border-color: rgba(255,193,7,.4); }
    .csf-select option { background: #242428; }
    .csf-result-count { font-size: 12px; color: rgba(255,255,255,.3); white-space: nowrap; margin-left: auto; }

    /* Layout */
    .csf-layout { display: grid; grid-template-columns: 1fr 300px; gap: 24px; align-items: start; }
    @media (max-width: 1024px) { .csf-layout { grid-template-columns: 1fr; } .csf-basket-aside { display: none !important; } }

    /* Card grid */
    .csf-grid {
      display: grid; grid-template-columns: repeat(4,1fr); gap: 16px;
      grid-column: 1;
    }
    @media (max-width: 1280px) { .csf-grid { grid-template-columns: repeat(3,1fr); } }
    @media (max-width: 860px)  { .csf-grid { grid-template-columns: repeat(2,1fr); gap: 12px; } }

    /* Card */
    .csf-card {
      background: var(--card-bg); border-radius: var(--radius);
      border: 1px solid rgba(255,255,255,.05); overflow: hidden;
      display: flex; flex-direction: column; cursor: pointer;
      transition: transform .25s cubic-bezier(.34,1.56,.64,1), box-shadow .25s ease, border-color .25s;
      animation: csf-fadeup .35s ease both;
    }
    @keyframes csf-fadeup { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
    .csf-card:hover { transform: translateY(-4px); box-shadow: 0 12px 32px rgba(0,0,0,.5); border-color: rgba(255,255,255,.1); }

    .csf-card-img-wrap { aspect-ratio: 5/7; background: #1a1a1e; overflow: hidden; flex-shrink: 0; }
    .csf-card-img { width:100%; height:100%; object-fit:contain; display:block; padding:5px; transition:transform .3s ease; }
    .csf-card:hover .csf-card-img { transform: scale(1.04); }
    .csf-card-img-ph { width:100%; height:100%; display:flex; align-items:center; justify-content:center; font-size:32px; opacity:.25; }

    .csf-card-line { height: 3px; flex-shrink: 0; }

    .csf-card-body { padding: 10px 12px 12px; display:flex; flex-direction:column; flex:1; gap:5px; }
    .csf-card-bottom { margin-top:auto; display:flex; flex-direction:column; gap:6px; padding-top:8px; }
    .csf-card-name { font-size: 13px; font-weight:700; line-height:1.3; color:var(--text);
      display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
    .csf-card-set { font-size:11px; color:rgba(242,242,242,.35); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .csf-card-badges { display:flex; flex-wrap:wrap; gap:3px; }
    .csf-badge {
      font-size:9px; font-weight:700; letter-spacing:.05em; text-transform:uppercase;
      padding:2px 6px; border-radius:4px;
      background:rgba(255,255,255,.07); color:rgba(242,242,242,.45);
      border:1px solid rgba(255,255,255,.06); white-space:nowrap;
    }
    /* Card-grid badge: truncate long rarity names (e.g. "Special Illustration Rare") */
    .csf-card-badges { overflow:hidden; }
    .csf-card-badges .csf-badge { min-width:0; overflow:hidden; text-overflow:ellipsis; }

    /* NM price + sparkline row on card */
    .csf-card-nm-row {
      display:flex; align-items:center; justify-content:space-between;
      gap:8px; margin-top:4px;
    }
    .csf-card-nm-left { display:flex; align-items:center; gap:5px; flex-shrink:0; }
    .csf-nm-label {
      font-size:8px; font-weight:800; letter-spacing:.04em;
      padding:2px 5px; border-radius:3px;
      background:rgba(74,222,128,.14); color:#4ade80;
    }
    .csf-card-nm-price { font-size:13px; font-weight:800; color:var(--accent); }
    .csf-sparkline { display:block; flex-shrink:0; opacity:.75; }

    /* Sell button */
    .csf-card-sell-btn {
      padding:8px 12px; border-radius:8px; border:none;
      font-size:12px; font-weight:800; cursor:pointer; font-family:inherit;
      letter-spacing:.03em; transition:opacity .15s, transform .1s; width:100%;
    }
    .csf-card-sell-btn:hover { opacity:.88; }
    .csf-card-sell-btn:active { transform:scale(.97); }

    /* Skeleton */
    .csf-card-skeleton { background:var(--card-bg); border-radius:var(--radius); overflow:hidden; border:1px solid rgba(255,255,255,.05); animation:csf-fadeup .35s ease both; }
    .csf-sk-img { aspect-ratio:5/7; background:linear-gradient(90deg,rgba(255,255,255,.04) 25%,rgba(255,255,255,.08) 50%,rgba(255,255,255,.04) 75%); background-size:200% 100%; animation:csf-shimmer 1.4s ease infinite; }
    .csf-sk-body { padding:12px; }
    .csf-sk-line { height:11px; border-radius:4px; margin-bottom:8px; background:linear-gradient(90deg,rgba(255,255,255,.04) 25%,rgba(255,255,255,.08) 50%,rgba(255,255,255,.04) 75%); background-size:200% 100%; animation:csf-shimmer 1.4s ease infinite; }
    @keyframes csf-shimmer { 0%{background-position:200% center} 100%{background-position:-200% center} }

    /* Empty */
    .csf-empty { grid-column:1/-1; padding:60px 24px; text-align:center; color:rgba(255,255,255,.28); font-size:15px; line-height:1.7; }
    .csf-empty-icon { font-size:40px; margin-bottom:16px; display:block; opacity:.5; }

    /* Pagination */
    .csf-pagination { display:flex; align-items:center; justify-content:center; gap:6px; margin-top:28px; padding-bottom:8px; grid-column:1/-1; }
    .csf-page-btn {
      width:36px; height:36px; border-radius:8px;
      border:1.5px solid rgba(255,255,255,.08); background:rgba(255,255,255,.04);
      color:var(--text); cursor:pointer; font-size:13px; font-weight:600; font-family:inherit;
      display:flex; align-items:center; justify-content:center; transition:all .15s;
    }
    .csf-page-btn:hover:not(:disabled) { border-color:rgba(255,255,255,.2); background:rgba(255,255,255,.07); }
    .csf-page-btn:disabled { opacity:.3; cursor:not-allowed; }
    .csf-page-btn.is-active { background:var(--accent); color:#111; border-color:var(--accent); font-weight:800; }
    .csf-page-ellipsis { font-size:13px; color:rgba(255,255,255,.3); padding:0 4px; }

    /* Basket aside */
    .csf-basket-aside { background:var(--card-bg); border-radius:var(--radius); border:1px solid rgba(255,255,255,.06); overflow:hidden; position:sticky; top:24px; }
    .csf-basket-head { padding:8px 18px 12px; border-bottom:1px solid rgba(255,255,255,.06); display:flex; justify-content:space-between; align-items:center; }
    .csf-basket-title { font-size:13px; font-weight:700; }
    .csf-basket-badge { font-size:11px; font-weight:700; padding:2px 8px; border-radius:20px; background:rgba(255,255,255,.07); color:rgba(255,255,255,.4); }
    .csf-basket-empty { padding:28px 18px 32px; text-align:center; display:flex; flex-direction:column; align-items:center; gap:8px; }
    .csf-basket-empty-icon {
      width:52px; height:52px; border-radius:50%;
      background:rgba(255,193,7,.05); border:1.5px dashed rgba(255,193,7,.2);
      display:flex; align-items:center; justify-content:center;
      color:rgba(255,193,7,.35); margin-bottom:4px;
    }
    .csf-basket-empty-title { font-size:13px; font-weight:700; color:rgba(255,255,255,.45); }
    .csf-basket-empty-sub { font-size:11px; color:rgba(255,255,255,.2); line-height:1.6; max-width:190px; }
    .csf-basket-empty-sub em { font-style:normal; color:rgba(255,193,7,.45); }
    .csf-basket-items { max-height:360px; overflow-y:auto; }
    .csf-basket-item { display:flex; flex-direction:column; align-items:stretch; gap:0; padding:12px 18px; border-bottom:1px solid rgba(255,255,255,.04); }
    .csf-basket-item-main { display:flex; justify-content:space-between; align-items:flex-start; gap:10px; }
    .csf-basket-item-info { flex:1; min-width:0; }
    .csf-basket-item-name { font-size:12px; font-weight:600; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .csf-basket-item-meta { font-size:11px; color:rgba(255,255,255,.35); margin-top:1px; }
    .csf-basket-item-right { display:flex; flex-direction:column; align-items:flex-end; gap:2px; flex-shrink:0; }
    .csf-basket-item-price { font-size:12px; font-weight:700; color:var(--accent); }

    /* Optional per-card condition photos — kept deliberately quiet so it never
       competes with the price or remove control above it. */
    .csf-photo-block { margin-top:7px; }
    .csf-photo-toggle { display:inline-flex; align-items:center; gap:5px; background:none; border:none; color:rgba(255,255,255,.42); font:inherit; font-size:11px; letter-spacing:.01em; cursor:pointer; padding:2px 0; transition:color .15s; }
    .csf-photo-toggle:hover { color:rgba(255,255,255,.75); }
    .csf-photo-toggle.is-open { color:rgba(255,255,255,.62); }
    .csf-photo-toggle-ic { opacity:.85; flex:none; }
    .csf-photo-hint { margin:5px 0 7px; font-size:10.5px; line-height:1.45; color:rgba(255,255,255,.35); }
    .csf-photo-thumbs { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
    .csf-photo-thumb { position:relative; width:44px; height:44px; border-radius:8px; overflow:hidden; flex:none; box-shadow:0 1px 3px rgba(0,0,0,.35); }
    .csf-photo-thumb img { width:100%; height:100%; object-fit:cover; display:block; }
    .csf-photo-remove { position:absolute; top:2px; right:2px; width:16px; height:16px; border:none; border-radius:50%; background:rgba(0,0,0,.65); color:#fff; font-size:12px; line-height:1; cursor:pointer; display:flex; align-items:center; justify-content:center; padding:0; transition:background .12s; }
    .csf-photo-remove:hover { background:rgba(220,38,38,.85); }
    .csf-photo-add { width:44px; height:44px; border:1.5px dashed rgba(255,255,255,.2); border-radius:8px; display:flex; align-items:center; justify-content:center; color:rgba(255,255,255,.35); font-size:18px; font-weight:300; cursor:pointer; flex:none; transition:border-color .15s, color .15s, background .15s; }
    .csf-photo-add:hover { border-color:rgba(255,255,255,.45); color:rgba(255,255,255,.7); background:rgba(255,255,255,.02); }
    .csf-basket-remove { background:none; border:none; color:rgba(255,255,255,.2); cursor:pointer; font-size:15px; padding:2px 3px; border-radius:3px; transition:color .1s; line-height:1; }
    .csf-basket-remove:hover { color:#f87171; }
    .csf-basket-foot { padding:14px 18px; border-top:1px solid rgba(255,255,255,.06); }
    .csf-basket-total-row { display:flex; justify-content:space-between; align-items:baseline; margin-bottom:12px; font-size:13px; color:rgba(255,255,255,.4); }
    .csf-basket-total-row strong { font-size:20px; font-weight:800; color:#fff; }

    /* Buttons */
    .csf-btn-primary {
      width:100%; padding:13px 18px; border-radius:10px; border:none;
      background:var(--accent); color:#111;
      font-size:14px; font-weight:800; cursor:pointer; font-family:inherit;
      letter-spacing:.03em; transition:opacity .15s, transform .1s;
    }
    .csf-btn-primary:disabled { opacity:.35; cursor:not-allowed; }
    .csf-btn-primary:not(:disabled):hover { opacity:.88; }
    .csf-btn-primary:not(:disabled):active { transform:scale(.98); }

    .csf-btn-ghost {
      padding:12px 24px; border-radius:10px;
      border:1.5px solid rgba(255,255,255,.1); background:rgba(255,255,255,.04);
      color:rgba(255,255,255,.8); font-size:14px; font-weight:600;
      cursor:pointer; font-family:inherit; transition:all .15s;
    }
    .csf-btn-ghost:hover { background:rgba(255,255,255,.08); border-color:rgba(255,255,255,.2); }

    /* Mobile FAB */
    .csf-fab { position:fixed; bottom:20px; right:16px; z-index:400; display:none; }
    @media (max-width:1024px) { .csf-fab { display:block; } }
    .csf-fab-btn {
      display:flex; align-items:center; gap:8px; padding:13px 18px; border-radius:50px; border:none;
      background:var(--accent); color:#111; font-size:13px; font-weight:800;
      cursor:pointer; font-family:inherit;
      box-shadow:0 4px 20px rgba(255,193,7,.35); transition:transform .1s, box-shadow .1s;
    }
    .csf-fab-btn.is-empty {
      background:#2a2a2e;
      color:rgba(255,255,255,.9);
      border:1.5px solid rgba(255,193,7,.35);
      box-shadow:0 4px 18px rgba(0,0,0,.55), 0 0 0 1px rgba(255,193,7,.08);
      animation:csf-fab-idle 3s ease-in-out infinite;
    }
    @keyframes csf-fab-idle {
      0%,100% { box-shadow:0 4px 18px rgba(0,0,0,.55), 0 0 0 1px rgba(255,193,7,.08); }
      50%      { box-shadow:0 4px 22px rgba(0,0,0,.6),  0 0 0 3px rgba(255,193,7,.14); }
    }
    .csf-fab-btn:active { transform:scale(.95); }
    .csf-fab-count { background:#111; color:var(--accent); width:22px; height:22px; border-radius:50%; font-size:11px; font-weight:800; display:flex; align-items:center; justify-content:center; }

    /* Panel */
    .csf-panel {
      position:fixed; top:0; right:0; width:min(440px,100vw); height:100dvh;
      background:#1e1e22; border-left:1px solid rgba(255,255,255,.08);
      z-index:500; overflow-y:auto; transform:translateX(110%);
      transition:transform .32s cubic-bezier(.33,1,.68,1);
      display:flex; flex-direction:column;
    }
    .csf-panel.is-open { transform:translateX(0); }
    .csf-panel-bd { position:fixed; inset:0; background:rgba(0,0,0,.65); z-index:499; opacity:0; pointer-events:none; transition:opacity .3s; }
    .csf-panel-bd.is-open { opacity:1; pointer-events:all; }

    @media (max-width:600px) {
      .csf-panel { top:auto; right:0; bottom:0; left:0; width:100%; height:90dvh; border-left:none; border-top:1px solid rgba(255,255,255,.1); border-radius:20px 20px 0 0; transform:translateY(110%); }
      .csf-panel.is-open { transform:translateY(0); }
    }

    .csf-panel-header { display:flex; align-items:center; justify-content:space-between; padding:16px 20px; border-bottom:1px solid rgba(255,255,255,.06); position:sticky; top:0; background:#1e1e22; z-index:1; flex-shrink:0; }
    .csf-panel-title { font-size:14px; font-weight:700; }
    .csf-panel-close { width:32px; height:32px; border-radius:50%; border:1.5px solid rgba(255,255,255,.1); background:none; color:rgba(255,255,255,.5); cursor:pointer; font-size:16px; display:flex; align-items:center; justify-content:center; transition:all .15s; }
    .csf-panel-close:hover { background:rgba(255,255,255,.07); color:#fff; }

    .csf-panel-body { padding:20px; flex:1; overflow-y:auto; }
    .csf-panel-card-row { display:flex; gap:16px; margin-bottom:22px; }
    .csf-panel-img { width:90px; border-radius:7px; object-fit:contain; box-shadow:0 8px 24px rgba(0,0,0,.5); flex-shrink:0; background:#1a1a1e; }
    .csf-panel-card-info { flex:1; min-width:0; }
    .csf-panel-card-name { font-size:17px; font-weight:800; line-height:1.2; margin-bottom:5px; }
    .csf-panel-card-set { font-size:12px; color:rgba(255,255,255,.4); margin-bottom:8px; }

    .csf-panel-section { margin-bottom:20px; }
    .csf-panel-label { font-size:10px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:rgba(255,255,255,.3); margin-bottom:10px; }

    /* Panel NM banner */
    .csf-panel-nm-banner {
      display:flex; align-items:center; justify-content:space-between; gap:12px;
      margin-bottom:20px; padding:12px 14px;
      background:rgba(74,222,128,.05); border:1px solid rgba(74,222,128,.14);
      border-radius:10px;
    }
    .csf-panel-nm-info { display:flex; align-items:center; gap:8px; flex:1; min-width:0; }
    .csf-panel-nm-tag {
      font-size:10px; font-weight:800; letter-spacing:.06em; padding:3px 7px;
      border-radius:4px; background:rgba(74,222,128,.15); color:#4ade80; flex-shrink:0;
    }
    .csf-panel-nm-label { font-size:11px; color:rgba(255,255,255,.35); line-height:1.4; }
    .csf-panel-nm-spark { flex-shrink:0; opacity:.65; }

    .csf-qty-row { display:flex; align-items:center; gap:12px; }
    .csf-qty-btn { width:38px; height:38px; border-radius:8px; border:1.5px solid rgba(255,255,255,.1); background:rgba(255,255,255,.04); color:#fff; font-size:20px; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .1s; font-family:inherit; line-height:1; }
    .csf-qty-btn:hover { background:rgba(255,255,255,.08); border-color:rgba(255,255,255,.2); }
    .csf-qty-val { font-size:18px; font-weight:700; min-width:32px; text-align:center; }

    .csf-panel-cta { padding:16px 20px 20px; border-top:1px solid rgba(255,255,255,.06); flex-shrink:0; background:#1e1e22; }
    .csf-panel-price-row { display:flex; justify-content:space-between; align-items:baseline; margin-bottom:12px; }
    .csf-panel-price-label { font-size:12px; color:rgba(255,255,255,.4); }
    .csf-panel-price-val { font-size:22px; font-weight:800; color:var(--accent); }

    /* Drawer */
    .csf-drawer { position:fixed; bottom:0; left:0; right:0; background:#1e1e22; border-top:1px solid rgba(255,255,255,.1); border-radius:20px 20px 0 0; z-index:500; max-height:85dvh; overflow:hidden; display:none; flex-direction:column; transform:translateY(110%); transition:transform .32s cubic-bezier(.33,1,.68,1); }
    .csf-drawer.is-open { transform:translateY(0); }
    @media (max-width:1024px) { .csf-drawer { display:flex; } }
    .csf-drawer-bd { position:fixed; inset:0; background:rgba(0,0,0,.6); z-index:499; opacity:0; pointer-events:none; transition:opacity .3s; }
    .csf-drawer-bd.is-open { opacity:1; pointer-events:all; }
    .csf-drawer-top { display:flex; align-items:center; justify-content:center; padding:10px 16px 4px; flex-shrink:0; }
    .csf-drawer-handle { width:40px; height:4px; border-radius:2px; background:rgba(255,255,255,.14); flex-shrink:0; }
    .csf-basket-head-right { display:flex; align-items:center; gap:8px; }
    .csf-drawer-close-btn {
      width:28px; height:28px; border-radius:50%; flex-shrink:0;
      border:1.5px solid rgba(255,255,255,.12); background:rgba(255,255,255,.06);
      color:rgba(255,255,255,.55); cursor:pointer;
      display:flex; align-items:center; justify-content:center;
      transition:background .15s, border-color .15s, color .15s;
    }
    .csf-drawer-close-btn:hover { background:rgba(255,255,255,.12); border-color:rgba(255,255,255,.25); color:#fff; }
    .csf-drawer-inner { padding:0 0 20px; overflow-y:auto; flex:1; }

    /* Sync bar */
    .csf-sync-bar { grid-column:1/-1; padding:10px; text-align:center; font-size:11px; color:rgba(255,255,255,.2); }

    /* Checkout */
    .csf-checkout-wrap { max-width:620px; margin:0 auto; padding:40px 20px 60px; }
    .csf-back-btn { background:none; border:none; color:rgba(255,255,255,.35); font-size:14px; cursor:pointer; display:flex; align-items:center; gap:6px; margin-bottom:24px; padding:0; font-family:inherit; transition:color .1s; }
    .csf-back-btn:hover { color:rgba(255,255,255,.8); }
    .csf-checkout-title { font-size:24px; font-weight:900; margin-bottom:28px; }
    .csf-form-group { margin-bottom:16px; }
    .csf-form-label { display:block; font-size:11px; font-weight:700; letter-spacing:.07em; text-transform:uppercase; color:rgba(255,255,255,.35); margin-bottom:7px; }
    .csf-form-input { width:100% !important; padding:13px 15px !important; border-radius:10px !important; border:1.5px solid rgba(255,255,255,.08) !important; background:rgba(255,255,255,.04) !important; color:var(--text) !important; font-size:15px !important; font-family:inherit !important; outline:none !important; box-shadow:none !important; transition:border-color .2s, box-shadow .2s !important; }
    .csf-form-input:focus { border-color:rgba(255,193,7,.4) !important; box-shadow:0 0 0 3px rgba(255,193,7,.08) !important; }
    .csf-postage-opts { display:flex; flex-direction:column; gap:8px; }
    .csf-postage-opt { display:flex; align-items:center; gap:14px; padding:13px 15px; border-radius:10px; border:1.5px solid rgba(255,255,255,.07); cursor:pointer; transition:border-color .15s; }
    .csf-postage-opt:hover { border-color:rgba(255,255,255,.15); }
    .csf-postage-opt.is-sel { border-color:rgba(255,193,7,.45); background:rgba(255,193,7,.04); }
    .csf-postage-opt input { accent-color:var(--accent); flex-shrink:0; }
    .csf-postage-opt-name { font-size:14px; font-weight:600; }
    .csf-postage-opt-desc { font-size:12px; color:rgba(255,255,255,.35); margin-top:1px; }
    .csf-payment-opts { display:flex; flex-direction:column; gap:8px; }
    .csf-payment-opt { display:flex; align-items:center; gap:14px; padding:13px 15px; border-radius:10px; border:1.5px solid rgba(255,255,255,.07); cursor:pointer; transition:border-color .15s, background .15s; }
    .csf-payment-opt:hover { border-color:rgba(255,255,255,.15); }
    .csf-payment-opt.is-sel { border-color:rgba(255,193,7,.45); background:rgba(255,193,7,.04); }
    .csf-payment-opt.is-credit { border-color:rgba(255,193,7,.2); }
    .csf-payment-opt.is-credit.is-sel { border-color:rgba(255,193,7,.6); background:rgba(255,193,7,.06); }
    .csf-payment-opt input { accent-color:var(--accent); flex-shrink:0; }
    .csf-payment-opt-name { font-size:14px; font-weight:600; display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
    .csf-payment-opt-desc { font-size:12px; color:rgba(255,255,255,.35); margin-top:1px; }
    .csf-payment-badge { font-size:10px; font-weight:700; letter-spacing:.04em; text-transform:uppercase; background:var(--accent); color:#111; padding:2px 7px; border-radius:20px; line-height:1.4; }
    .csf-payment-gdpr { font-size:12px; color:#888; line-height:1.6; margin:4px 0 0; padding:10px 13px; border-left:2px solid rgba(255,193,7,.3); background:rgba(255,193,7,.03); border-radius:0 8px 8px 0; }
    .csf-paypal-block { margin:2px 0 4px; padding:10px 13px; border-left:2px solid rgba(255,193,7,.3); background:rgba(255,193,7,.03); border-radius:0 8px 8px 0; }
    .csf-paypal-same { display:flex; align-items:center; gap:8px; font-size:13px; color:rgba(255,255,255,.75); cursor:pointer; }
    .csf-paypal-same input { accent-color:var(--accent); width:15px; height:15px; flex:0 0 auto; }
    .csf-order-summary { background:rgba(255,255,255,.03); border:1.5px solid rgba(255,255,255,.07); border-radius:12px; padding:16px; margin:22px 0; }
    .csf-order-summary-title { font-size:10px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:rgba(255,255,255,.28); margin-bottom:12px; }
    .csf-order-row { display:flex; justify-content:space-between; font-size:14px; padding:3px 0; }
    .csf-order-name { color:rgba(255,255,255,.45); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:70%; }
    .csf-order-divider { height:1px; background:rgba(255,255,255,.07); margin:10px 0; }
    .csf-order-total { font-size:15px; font-weight:700; }
    .csf-error-msg { padding:13px 15px; border-radius:10px; background:rgba(248,113,113,.08); border:1px solid rgba(248,113,113,.25); color:#fca5a5; font-size:14px; margin-top:14px; }
    .csf-age-confirm { display:flex; align-items:flex-start; gap:10px; margin:18px 0 4px; font-size:14px; color:rgba(255,255,255,.82); cursor:pointer; line-height:1.5; }
    .csf-age-confirm input { width:18px; height:18px; margin:1px 0 0; accent-color:var(--accent); cursor:pointer; flex:0 0 auto; }
    .csf-age-hint { font-size:12px; color:rgba(255,255,255,.45); text-align:center; margin:8px 0 0; }
    .csf-age-terms { font-size:12px; color:rgba(255,255,255,.45); margin:6px 0 0; padding-left:28px; }
    .csf-age-terms a { color:var(--accent); text-decoration:underline; }

    /* Success */
    .csf-success-wrap { max-width:480px; margin:0 auto; padding:80px 24px; text-align:center; }
    .csf-success-icon { width:72px; height:72px; border-radius:50%; background:rgba(74,222,128,.1); border:2px solid #4ade80; display:flex; align-items:center; justify-content:center; margin:0 auto 24px; font-size:28px; }
    .csf-success-title { font-size:26px; font-weight:900; margin-bottom:12px; }
    .csf-success-ref { display:inline-block; padding:7px 16px; border-radius:20px; background:rgba(255,255,255,.05); border:1px solid rgba(255,255,255,.1); font-size:13px; font-family:monospace; margin-bottom:16px; }
    .csf-success-body { font-size:14px; color:rgba(255,255,255,.45); max-width:380px; margin:0 auto 28px; line-height:1.7; }

    /* Spinner */
    .csf-spin { display:inline-block; width:15px; height:15px; border:2px solid rgba(255,255,255,.25); border-top-color:currentColor; border-radius:50%; animation:csf-spin .6s linear infinite; vertical-align:middle; }
    @keyframes csf-spin { to { transform:rotate(360deg); } }

    /* Mobile polish */
    @media (max-width:768px) {
      .csf-hero { padding:32px 16px 28px; }
      .csf-main { padding:0 12px 100px; }
      .csf-filter-row { gap:8px; }
      .csf-card-body { padding:8px 10px 10px; gap:4px; }
      .csf-card-name { font-size:12px; }
      .csf-card-set { font-size:10px; }
      .csf-card-price-val { font-size:10px; }
      .csf-card-sell-btn { font-size:11px; padding:7px 10px; }
      .csf-result-count { display:none; }
      /* Shrink sparkline on mobile so it fits within the narrow card nm-row */
      .csf-sparkline { width:44px; height:17px; flex-shrink:1; }
      .csf-card-nm-row { gap:6px; overflow:hidden; }
    }
    @media (max-width:480px) {
      .csf-filter-row { flex-wrap:nowrap; overflow-x:auto; padding-bottom:4px; -webkit-overflow-scrolling:touch; }
      .csf-filter-row::-webkit-scrollbar { display:none; }
      .csf-select { min-width:100px; font-size:12px; }
      /* Extra-narrow phones (≤480px): tighten sparkline further */
      .csf-sparkline { width:36px; height:14px; }
      .csf-card-nm-row { gap:4px; }
    }

    /* ── Game tabs row ─────────────────────────────────────────────────────── */
    .csf-game-tabs-row { display:flex; align-items:center; gap:16px; margin-bottom:20px; flex-wrap:wrap; }
    .csf-game-tabs { display:flex; gap:10px; flex-wrap:wrap; }

    /* ── Price trend badge ─────────────────────────────────────────────────── */
    .csf-trend {
      font-size:8px; font-weight:800; letter-spacing:.02em;
      padding:1px 4px; border-radius:3px; flex-shrink:0; line-height:1.5;
      margin-left:1px;
    }
    .csf-trend--up   { color:#4ade80; background:rgba(74,222,128,.12); }
    .csf-trend--down { color:#f87171; background:rgba(248,113,113,.12); }
    .csf-trend--flat { color:rgba(255,255,255,.22); background:rgba(255,255,255,.05); }

    /* ── Condition tooltip on card grid dots ──────────────────────────────── */
    .csf-cond-dot[data-tooltip] { position:relative; cursor:default; }
    .csf-cond-dot[data-tooltip]::after {
      content:attr(data-tooltip);
      position:absolute; bottom:calc(100% + 6px); left:50%;
      transform:translateX(-50%);
      background:#2e2e33; color:rgba(255,255,255,.9);
      font-size:10px; font-weight:500; white-space:nowrap;
      padding:4px 9px; border-radius:6px;
      pointer-events:none; opacity:0; transition:opacity .15s;
      z-index:20; border:1px solid rgba(255,255,255,.1);
      box-shadow:0 4px 12px rgba(0,0,0,.45);
    }
    .csf-cond-dot[data-tooltip]:hover::after { opacity:1; }

    /* ── Condition card criteria text ─────────────────────────────────────── */
    .csf-cond-card-criteria {
      font-size:8px; color:rgba(255,255,255,.22); line-height:1.4;
      margin-bottom:6px; min-height:20px; text-align:center;
    }

    /* ── Minimum order warning ────────────────────────────────────────────── */
    .csf-min-warn {
      display:flex; align-items:flex-start; gap:7px;
      padding:9px 12px; margin-bottom:10px;
      border-radius:8px;
      background:rgba(251,191,36,.07);
      border:1px solid rgba(251,191,36,.2);
      color:rgba(251,191,36,.85); font-size:11px; line-height:1.5;
    }
    .csf-min-warn svg { flex-shrink:0; margin-top:1px; }
    .csf-min-warn strong { color:rgb(251,191,36); }

    /* ── Track submission link ────────────────────────────────────────────── */
    .csf-track-link {
      background:none; border:none; padding:6px 10px;
      color:rgba(255,255,255,.28); font-size:12px; font-weight:600;
      cursor:pointer; font-family:inherit;
      display:inline-flex; align-items:center; gap:6px;
      border-radius:8px; transition:color .15s, background .15s;
      white-space:nowrap; margin-left:auto;
    }
    .csf-track-link:hover { color:rgba(255,193,7,.75); background:rgba(255,193,7,.06); }

    /* ── Submission tracker view ──────────────────────────────────────────── */
    .csf-track-wrap { max-width:560px; margin:0 auto; padding:40px 20px 60px; }
    .csf-track-sub { font-size:14px; color:rgba(255,255,255,.35); margin-bottom:28px; line-height:1.65; }
    .csf-track-form { margin-bottom:24px; }
    .csf-track-input-row { display:flex; gap:10px; }
    .csf-track-input { flex:1 !important; font-family:monospace !important; letter-spacing:.03em !important; font-size:13px !important; }
    .csf-track-submit-btn { width:auto !important; white-space:nowrap; flex-shrink:0; padding:0 22px !important; }
    .csf-track-error { color:#fca5a5; font-size:13px; margin-top:10px; }

    .csf-tracker-result {
      background:var(--card-bg); border:1px solid rgba(255,255,255,.07);
      border-radius:14px; padding:22px 22px 18px; margin-top:6px;
    }
    .csf-tracker-ref {
      font-family:monospace; font-size:14px; font-weight:700;
      color:var(--accent); letter-spacing:.07em; margin-bottom:24px;
      padding:6px 12px; background:rgba(255,193,7,.07); border-radius:6px; display:inline-block;
    }
    .csf-tracker-steps { display:flex; flex-direction:column; gap:0; }

    .csf-step { display:flex; gap:14px; }
    .csf-step-left { display:flex; flex-direction:column; align-items:center; flex-shrink:0; }
    .csf-step-dot {
      width:24px; height:24px; border-radius:50%;
      background:rgba(255,255,255,.05); border:2px solid rgba(255,255,255,.1);
      display:flex; align-items:center; justify-content:center;
      color:#fff; transition:all .25s;
    }
    .csf-step.is-done   .csf-step-dot { background:rgba(74,222,128,.12); border-color:#4ade80; color:#4ade80; }
    .csf-step.is-current .csf-step-dot { background:rgba(255,193,7,.1); border-color:var(--accent); }
    .csf-step-line {
      width:2px; flex:1; min-height:20px; margin:3px 0;
      background:rgba(255,255,255,.07); transition:background .25s;
    }
    .csf-step.is-done .csf-step-line { background:rgba(74,222,128,.25); }
    .csf-step-body { padding-bottom:20px; flex:1; }
    .csf-step-label { font-size:13px; font-weight:700; color:rgba(255,255,255,.4); padding-top:3px; transition:color .2s; }
    .csf-step.is-done .csf-step-label,
    .csf-step.is-current .csf-step-label { color:rgba(255,255,255,.85); }
    .csf-step-desc { font-size:12px; color:rgba(255,255,255,.35); margin-top:4px; line-height:1.5; }
    .csf-step-pulse {
      width:9px; height:9px; border-radius:50%; background:var(--accent);
      animation:csf-pulse 1.6s ease-in-out infinite;
    }
    @keyframes csf-pulse {
      0%,100% { opacity:1; transform:scale(1); }
      50%      { opacity:.45; transform:scale(.65); }
    }
    .csf-tracker-meta { font-size:12px; color:rgba(255,255,255,.28); margin-top:6px; }

    .csf-tracker-notfound {
      text-align:center; padding:36px 24px;
      background:rgba(255,255,255,.02); border:1px solid rgba(255,255,255,.06);
      border-radius:14px; margin-top:6px;
    }
    .csf-tracker-notfound-icon {
      width:50px; height:50px; border-radius:50%;
      background:rgba(248,113,113,.07); border:1.5px solid rgba(248,113,113,.2);
      color:rgba(248,113,113,.55); display:flex; align-items:center; justify-content:center;
      margin:0 auto 14px;
    }
    .csf-tracker-notfound-title { font-size:15px; font-weight:700; margin-bottom:8px; }
    .csf-tracker-notfound-sub { font-size:13px; color:rgba(255,255,255,.35); line-height:1.65; }
    .csf-tracker-notfound-sub code { font-family:monospace; background:rgba(255,255,255,.08); padding:2px 6px; border-radius:4px; }

    @media (max-width:520px) {
      .csf-track-input-row { flex-direction:column; }
      .csf-track-submit-btn { width:100% !important; }
    }
  `;

  // ── Public API ────────────────────────────────────────────────────────────

  window.CurioSell = {
    init(config = {}) {
      apiUrl = (config.apiUrl || 'http://localhost:3001').replace(/\/$/, '');
      if (config.gameLogos) {
        Object.entries(config.gameLogos).forEach(([id, url]) => {
          if (GAMES[id] && url) GAMES[id].logoUrl = url;
        });
      }
      root = document.getElementById('curio-sell');
      if (!root) { console.error('[CurioSell] #curio-sell not found'); return; }
      init();
    },
  };
})();
